import React, { useEffect, useState, useRef } from 'react';
import { collection, query, where, getDocs, updateDoc, doc, addDoc, serverTimestamp, onSnapshot, QuerySnapshot, DocumentData } from 'firebase/firestore';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../../config/firebaseWithFailover';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import styles from '../carrier/Notifications.module.css';
import RejectionOptionsModal from '../../components/shipper/RejectionOptionsModal';
import CounterOfferModal from '../../components/shipper/CounterOfferModal';
import { updateLoadRequestStatus, handleShipperRejection, acceptLoadForPO } from '../../services/notificationService';
import { Dialog } from '@reach/dialog';
import '@reach/dialog/styles.css';
import Logger from '../../services/logger';
import { NOTIFICATION_STATUS, PARTNER_REQUEST_STATUS } from '../../constants/notificationTypes';
import { SHIPPING_SCHEDULE_STATUS } from '../../constants/statusValues';

interface Notification {
  id: string;
  type: string;
  senderId: string;
  senderName: string;
  message: string;
  read: boolean;
  createdAt: Date;
  requiresAction?: boolean;
  loadDetails?: {
    pickupLocation: {
      address: string;
      city: string;
      state: string;
    };
    deliveryLocation: {
      address: string;
      city: string;
      state: string;
    };
    rate: number;
    poNumber?: string;
    isMarketplace?: boolean;
  };
  shipperId?: string;
  carrierId?: string;
}

interface ShipperNotificationsTrayProps {
  onClose: () => void;
}

const ShipperNotificationsTray: React.FC<ShipperNotificationsTrayProps> = ({ onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const trayRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsNotification, setDetailsNotification] = useState<Notification | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !showDetailsModal &&
        !showRejectionModal &&
        !showCounterOfferModal &&
        trayRef.current &&
        !trayRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, showDetailsModal, showRejectionModal, showCounterOfferModal]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getFailoverAwareAuth(), async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          // Query notifications for shipper using shipperId
          const shipperQuery = query(
            collection(getFailoverAwareDatabase(), 'notifications'),
            where('shipperId', '==', user.uid)
          );
          
          // Query notifications for shipper using userId (fallback)
          const userQuery = query(
            collection(getFailoverAwareDatabase(), 'notifications'),
            where('userId', '==', user.uid)
          );
          
          let shipperNotifications: Notification[] = [];
          let userNotifications: Notification[] = [];
          let shipperUnsubscribe: (() => void) | null = null;
          let userUnsubscribe: (() => void) | null = null;
          
          const processNotifications = () => {
            // Merge and deduplicate notifications
            const allNotifications = [...shipperNotifications, ...userNotifications];
            const uniqueNotifications = allNotifications.filter((notification, index, self) => 
              index === self.findIndex(n => n.id === notification.id)
            );
            
            // PRODUCTION FIX: Filter out notification types that are handled by dashboard "Action Required" cards
            // These should ONLY appear in the dashboard cards section, NOT in the bell tray
            // Dashboard handles: carrier_decline, make_offer, carrier_counter_offer
            const dashboardHandledTypes = ['carrier_decline', 'make_offer', 'carrier_counter_offer'];
            const bellOnlyNotifications = uniqueNotifications.filter(n => 
              !dashboardHandledTypes.includes(n.type)
            );
            
            Logger.debug('[DEBUG] Shipper notifications processed:', {
              shipperNotifications: shipperNotifications.length,
              userNotifications: userNotifications.length,
              total: uniqueNotifications.length,
              bellOnly: bellOnlyNotifications.length,
              filteredOut: uniqueNotifications.length - bellOnlyNotifications.length,
              types: uniqueNotifications.map(n => n.type),
              bellTypes: bellOnlyNotifications.map(n => n.type)
            });
            setNotifications(bellOnlyNotifications.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
            setLoading(false);
            setError(null);
          };
          
          shipperUnsubscribe = onSnapshot(shipperQuery, (snapshot: QuerySnapshot<DocumentData>) => {
            try {
              const notificationList: Notification[] = [];
              snapshot.forEach((doc) => {
                try {
                  const data = doc.data();
                  notificationList.push({
                    id: doc.id,
                    type: data.type || 'unknown',
                    senderId: data.senderId || data.carrierId || '',
                    senderName: data.senderName || 'Unknown',
                    message: data.message || '',
                    read: data.read || false,
                    createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
                    requiresAction: data.requiresAction || false,
                    loadDetails: data.loadDetails || null,
                    shipperId: data.shipperId || undefined,
                    carrierId: data.carrierId || undefined
                  });
                } catch (docError) {
                  Logger.error('[ShipperNotificationsTray] Error processing notification document:', docError as Error, { docId: doc.id });
                }
              });
              shipperNotifications = notificationList;
              processNotifications();
            } catch (snapshotError) {
              Logger.error('[ShipperNotificationsTray] Error processing shipper notification snapshot:', snapshotError as Error);
              setError('Failed to load notifications');
              setLoading(false);
            }
          });

          userUnsubscribe = onSnapshot(userQuery, (snapshot: QuerySnapshot<DocumentData>) => {
            try {
              const notificationList: Notification[] = [];
              snapshot.forEach((doc) => {
                try {
                  const data = doc.data();
                  notificationList.push({
                    id: doc.id,
                    type: data.type || 'unknown',
                    senderId: data.senderId || data.carrierId || '',
                    senderName: data.senderName || 'Unknown',
                    message: data.message || '',
                    read: data.read || false,
                    createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
                    requiresAction: data.requiresAction || false,
                    loadDetails: data.loadDetails || null,
                    shipperId: data.shipperId || undefined,
                    carrierId: data.carrierId || undefined
                  });
                } catch (docError) {
                  Logger.error('[ShipperNotificationsTray] Error processing notification document:', docError as Error, { docId: doc.id });
                }
              });
              userNotifications = notificationList;
              processNotifications();
            } catch (snapshotError) {
              Logger.error('[ShipperNotificationsTray] Error processing user notification snapshot:', snapshotError as Error);
              setError('Failed to load notifications');
              setLoading(false);
            }
          });

          return () => {
            if (shipperUnsubscribe) shipperUnsubscribe();
            if (userUnsubscribe) userUnsubscribe();
          };
        } catch (error) {
          Logger.error('[ShipperNotificationsTray] Error setting up notifications:', error as Error);
          setError('Failed to load notifications');
          setLoading(false);
        }
      } else {
        setCurrentUser(null);
        setNotifications([]);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleMarkAsRead = async (notificationId: string) => {
    const notificationRef = doc(getFailoverAwareDatabase(), 'notifications', notificationId);
    await updateDoc(notificationRef, { read: true });
    setNotifications(prev => prev.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    ));
  };

  const handleNotificationClick = (notification: Notification) => {
    if ((notification.type === 'carrier_reject' || notification.type === 'offer_rejected') && notification.requiresAction) {
      setSelectedNotification(notification);
      setShowRejectionModal(true);
    } else if (notification.type === 'carrier_counter_offer' && notification.requiresAction) {
      setSelectedNotification(notification);
      setShowCounterOfferModal(true);
    } else if (notification.type === 'make_offer' && notification.requiresAction) {
      // Marketplace offers with action required - show accept/reject modal
      setSelectedNotification(notification);
      setShowCounterOfferModal(true);
    } else if (notification.type === 'carrier_accept' && notification.requiresAction) {
      // Marketplace carrier acceptance - process directly
      setSelectedNotification(notification);
      handleAcceptCounterOffer();
    } else if (notification.type === 'make_offer') {
      // Marketplace offers without action required - show details only
      setDetailsNotification(notification);
      setShowDetailsModal(true);
    } else if (notification.type === 'offer_rejected') {
      setDetailsNotification(notification);
      setShowDetailsModal(true);
    }
  };

  const handleSelectNewCarrier = () => {
    if (selectedNotification?.loadDetails) {
      navigate('/shipper/partners', { 
        state: { 
          loadDetails: selectedNotification.loadDetails,
          fromRejection: true 
        }
      });
    }
    setShowRejectionModal(false);
  };

  const handlePlaceInMarketplace = () => {
    if (selectedNotification?.loadDetails) {
      navigate('/shipper/marketplace', { 
        state: { 
          loadDetails: selectedNotification.loadDetails,
          fromRejection: true 
        }
      });
    }
    setShowRejectionModal(false);
  };

  const handleAcceptCounterOffer = async () => {
    if (selectedNotification?.id) {
      try {
        if (selectedNotification.loadDetails?.poNumber) {
          if (selectedNotification.type === 'make_offer') {
            // Marketplace offer acceptance - pass notification ID for direct update
            await updateLoadRequestStatus(selectedNotification.loadDetails.poNumber, NOTIFICATION_STATUS.ACCEPTED, undefined, undefined, selectedNotification.id);
          } else if (selectedNotification.type === 'carrier_accept') {
            // Marketplace load acceptance - process directly
            const carrierId = selectedNotification.carrierId;
            if (!carrierId) {
              throw new Error('Carrier ID not found in acceptance notification');
            }
            Logger.debug('[DEBUG] Processing carrier_accept notification:', {
              poNumber: selectedNotification.loadDetails.poNumber,
              carrierId: carrierId,
              isPartnered: false
            });
            await acceptLoadForPO(selectedNotification.loadDetails.poNumber, carrierId, false);
            Logger.debug('[DEBUG] Successfully processed carrier_accept notification');
          } else if (selectedNotification.type === 'carrier_counter_offer') {
            // FIX 1: Counter offer acceptance - pass counter offer amount
            const carrierId = selectedNotification.carrierId;
            if (!carrierId) {
              throw new Error('Carrier ID not found in counter offer');
            }
            
            // FIX: Get counter offer rate from notification
            const counterOfferRate = selectedNotification.loadDetails?.rate;
            Logger.debug('[ShipperNotificationsTray] Accepting counter offer with rate:', { rate: counterOfferRate });
            
            // Accept the load using the proper service with counter offer amount
            // FIX: acceptLoadForPO now handles all updates (PO, load, shipping schedule, etc.)
            await acceptLoadForPO(
              selectedNotification.loadDetails.poNumber, 
              carrierId, 
              false, // Marketplace carrier
              counterOfferRate // FIX: Pass counter offer amount
            );
            
            // Create acceptance notification for carrier
            await addDoc(collection(getFailoverAwareDatabase(), 'notifications'), {
              carrierId: carrierId,
              recipientId: carrierId,
              poNumber: selectedNotification.loadDetails.poNumber,
              status: NOTIFICATION_STATUS.ACCEPTED,
              type: 'shipper_accept_counter',
              message: `Shipper accepted your counter offer of $${counterOfferRate?.toLocaleString() || '0'} for PO ${selectedNotification.loadDetails.poNumber}`,
              read: false,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
        
        // Mark notification as read and remove requiresAction
        const notificationRef = doc(getFailoverAwareDatabase(), 'notifications', selectedNotification.id);
        await updateDoc(notificationRef, { 
          read: true,
          status: NOTIFICATION_STATUS.ACCEPTED,
          requiresAction: false,
          updatedAt: serverTimestamp()
        });
        
        // Update partner request status to accepted (for carrier_counter_offer types)
        if (selectedNotification.type === 'carrier_counter_offer' && selectedNotification.carrierId && selectedNotification.loadDetails?.poNumber) {
          const partnerRequestQuery = query(
            collection(getFailoverAwareDatabase(), 'partnerRequests'),
            where('poNumber', '==', selectedNotification.loadDetails.poNumber),
            where('carrierId', '==', selectedNotification.carrierId)
          );
          const partnerRequestSnap = await getDocs(partnerRequestQuery);
          
          for (const partnerRequestDoc of partnerRequestSnap.docs) {
            await updateDoc(doc(getFailoverAwareDatabase(), 'partnerRequests', partnerRequestDoc.id), {
              status: PARTNER_REQUEST_STATUS.ACCEPTED,
              updatedAt: serverTimestamp()
            });
          }
        }
        
        setNotifications(prev => prev.map(n => 
          n.id === selectedNotification.id ? { ...n, read: true } : n
        ));
        
        setShowCounterOfferModal(false);
        
        // Navigate to Driver Updates page for counter offers
        if (selectedNotification.type === 'carrier_counter_offer') {
          navigate('/shipper/driver-updates');
        }
      } catch (error) {
        Logger.error('[ShipperNotificationsTray] Error accepting offer:', error as Error);
        alert('Failed to accept offer. Please try again.');
      }
    }
  };

  const handleRejectCounterOffer = async () => {
    if (selectedNotification?.id) {
      try {
        // Handle both marketplace offers and partner counter offers
        if (selectedNotification.loadDetails?.poNumber) {
          if (selectedNotification.type === 'make_offer') {
            // Marketplace offer rejection - pass notification ID for direct update
            await updateLoadRequestStatus(selectedNotification.loadDetails.poNumber, NOTIFICATION_STATUS.REJECTED, undefined, undefined, selectedNotification.id);
          } else if (selectedNotification.type === 'carrier_counter_offer') {
            // Partner counter offer rejection - use production flow
            const carrierId = selectedNotification.carrierId;
            if (!carrierId) {
              throw new Error('Carrier ID not found in counter offer');
            }
            
            const poNumber = selectedNotification.loadDetails.poNumber;
            
            // FIX: Check if this is a multi-load child PO and return it to marketplace
            const poQuery = query(
              collection(getFailoverAwareDatabase(), 'purchaseOrders'),
              where('poNumber', '==', poNumber)
            );
            const poSnap = await getDocs(poQuery);
            
            if (!poSnap.empty) {
              const poData = poSnap.docs[0].data();
              
              // Check if this is a child PO (has parentPONumber)
              if (poData.isMultiLoad === true && poData.parentPONumber) {
                // This is a child PO - return it to marketplace
                const { multiLoadService } = await import('../../services/multiLoadService');
                const result = await multiLoadService.returnChildPOToMarketplace(poNumber, 'purchaseOrders');
                
                if (!result.success) {
                  Logger.error('[handleRejectCounterOffer] Failed to return child PO to marketplace:', new Error(result.error || 'Unknown error'), { result });
                  // Continue with regular rejection flow as fallback
                } else {
                  Logger.debug('[handleRejectCounterOffer] Child PO returned to marketplace, remainingLoads:', { remainingLoads: result.remainingLoads });
                }
              }
            }
            
            // Update load status to remove carrier assignment but keep in marketplace
            const loadsQuery = query(collection(getFailoverAwareDatabase(), 'loads'), where('poNumber', '==', poNumber));
            const loadsSnap = await getDocs(loadsQuery);
            
            for (const loadDoc of loadsSnap.docs) {
              // Filter out parent marketplace loads - only update child-specific loads
              const loadData = loadDoc.data();
              if (!(loadData.isMultiLoad === true && !loadData.parentPONumber)) {
                await updateDoc(doc(getFailoverAwareDatabase(), 'loads', loadDoc.id), {
                  carrierId: null,
                  status: SHIPPING_SCHEDULE_STATUS.OPEN,
                  isMarketplace: true,
                  updatedAt: serverTimestamp()
                });
              }
            }
            
            // Create rejection notification for carrier
            await addDoc(collection(getFailoverAwareDatabase(), 'notifications'), {
              carrierId: carrierId,
              recipientId: carrierId,
              poNumber: selectedNotification.loadDetails.poNumber,
              status: NOTIFICATION_STATUS.REJECTED,
              type: 'shipper_reject_counter',
              message: `Shipper rejected your counter offer of $${selectedNotification.loadDetails.rate?.toLocaleString() || '0'} for PO ${selectedNotification.loadDetails.poNumber}`,
              read: false,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
        
        // Mark notification as read and remove requiresAction
        const notificationRef = doc(getFailoverAwareDatabase(), 'notifications', selectedNotification.id);
        await updateDoc(notificationRef, { 
          read: true,
          status: NOTIFICATION_STATUS.REJECTED,
          requiresAction: false,
          updatedAt: serverTimestamp()
        });
        
        // Update partner request status to rejected (for carrier_counter_offer types)
        if (selectedNotification.type === 'carrier_counter_offer' && selectedNotification.carrierId && selectedNotification.loadDetails?.poNumber) {
          const partnerRequestQuery = query(
            collection(getFailoverAwareDatabase(), 'partnerRequests'),
            where('poNumber', '==', selectedNotification.loadDetails.poNumber),
            where('carrierId', '==', selectedNotification.carrierId)
          );
          const partnerRequestSnap = await getDocs(partnerRequestQuery);
          
          for (const partnerRequestDoc of partnerRequestSnap.docs) {
            await updateDoc(doc(getFailoverAwareDatabase(), 'partnerRequests', partnerRequestDoc.id), {
              status: PARTNER_REQUEST_STATUS.REJECTED,
              updatedAt: serverTimestamp()
            });
          }
        }
        
        setNotifications(prev => prev.map(n => 
          n.id === selectedNotification.id ? { ...n, read: true } : n
        ));
        
        setShowCounterOfferModal(false);
        
        // Navigate to partners page for counter offer rejections
        if (selectedNotification.type === 'carrier_counter_offer') {
          navigate('/shipper/partners', {
            state: {
              poData: {
                poNumber: selectedNotification.loadDetails?.poNumber,
                pickupLocation: selectedNotification.loadDetails?.pickupLocation?.address || 'Unknown',
                deliveryLocation: selectedNotification.loadDetails?.deliveryLocation?.address || 'Unknown',
                rate: selectedNotification.loadDetails?.rate || 0
              },
              fromRejection: true
            }
          });
        }
      } catch (error) {
        Logger.error('[ShipperNotificationsTray] Error rejecting offer:', error as Error);
        alert('Failed to reject offer. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading notifications...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <div>Error: {error}</div>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <div ref={trayRef} className={styles.notificationList} style={{ height: 'calc(100vh - 60px)', overflowY: 'auto', padding: '0 16px' }}>
        {notifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
            <div style={{ fontSize: '18px', marginBottom: '8px' }}>No notifications</div>
            <div>You're all caught up!</div>
          </div>
        ) : (
          notifications.map((n) => {
            const amount = n.loadDetails?.rate;
            const amountLabel = typeof amount === 'number' ? `$${amount.toLocaleString()}` : undefined;
            const po = n.loadDetails?.poNumber || '—';
            const created = n.createdAt?.toLocaleString ? n.createdAt.toLocaleString() : '';

            const badgeClass = n.type === 'make_offer'
              ? styles.typeOffer
              : n.type === 'carrier_counter_offer'
              ? styles.typeCounter
              : (n.type || '').includes('accept')
              ? styles.typeAccepted
              : (n.type || '').includes('reject')
              ? styles.typeRejected
              : styles.typeOffer;

            return (
              <div key={n.id} className={styles.card} onClick={() => handleNotificationClick(n)}>
                <div className={styles.cardHeader}>
                  <span className={styles.icon}>🔔</span>
                  <span className={`${styles.typeBadge} ${badgeClass}`}>{(n.type || 'notification').replace(/_/g,' ')}</span>
                </div>
                <div className={styles.actions}>
                  {!n.read && (
                    <button className={styles.btn} onClick={(e) => { e.stopPropagation(); handleMarkAsRead(n.id); }}>Mark read</button>
                  )}
                  <button className={styles.btnLink} onClick={(e) => { e.stopPropagation(); setDetailsNotification(n); setShowDetailsModal(true); }}>Details</button>
                </div>
                <div className={styles.messageRow}>
                  <span>{n.message}</span>
                </div>
                <div className={styles.metaRow}>
                  <span>{created}</span>
                  {amountLabel && <span>Amount: <span className={styles.amount}>{amountLabel}</span></span>}
                  <span>PO: {po}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && detailsNotification && (
        <Dialog isOpen={showDetailsModal} onDismiss={() => setShowDetailsModal(false)}>
          <div style={{ padding: '20px', maxWidth: '500px' }}>
            <h2 style={{ marginBottom: '16px', color: '#333' }}>Notification Details</h2>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>Message:</div>
              <div style={{ marginBottom: '16px' }}>{detailsNotification.message}</div>
              
              {detailsNotification.loadDetails && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: '8px' }}>Load Details:</div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>PO Number:</strong> {detailsNotification.loadDetails.poNumber}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Rate:</strong> ${detailsNotification.loadDetails.rate?.toLocaleString()}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Pickup:</strong> {[detailsNotification.loadDetails.pickupLocation?.address, detailsNotification.loadDetails.pickupLocation?.city, detailsNotification.loadDetails.pickupLocation?.state].filter(Boolean).join(', ')}
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <strong>Delivery:</strong> {[detailsNotification.loadDetails.deliveryLocation?.address, detailsNotification.loadDetails.deliveryLocation?.city, detailsNotification.loadDetails.deliveryLocation?.state].filter(Boolean).join(', ')}
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 18px', fontWeight: 600, fontSize: 16, flex: 1, cursor: 'pointer' }} onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedNotification && (
        <RejectionOptionsModal
          isOpen={showRejectionModal}
          onClose={() => setShowRejectionModal(false)}
          onSelectNewCarrier={handleSelectNewCarrier}
          onPlaceInMarketplace={handlePlaceInMarketplace}
          loadDetails={selectedNotification.loadDetails!}
        />
      )}

      {/* Counter Offer Modal */}
      {showCounterOfferModal && selectedNotification && selectedNotification.loadDetails && (
        <CounterOfferModal
          isOpen={showCounterOfferModal}
          onClose={() => setShowCounterOfferModal(false)}
          onAccept={handleAcceptCounterOffer}
          onReject={handleRejectCounterOffer}
          currentRate={selectedNotification.loadDetails.rate}
          counterOffer={selectedNotification.loadDetails.rate}
          loadDetails={{
            ...selectedNotification.loadDetails,
            carrierName: selectedNotification.senderName || 'Unknown Carrier'
          }}
        />
      )}
    </>
  );
};

export default ShipperNotificationsTray;
